/**
 * useRooms.ts
 * React Query hooks for room management (chat rooms for clubs, events, training groups)
 *
 * #2066 Slice B: the `Room` shape, `getRoomCategory`, and the club-rooms
 * fetch moved to the `lib/api/rooms.ts` domain module (mobile-contract §1 —
 * native wraps domain modules, not web hooks). Re-exported here so existing
 * web imports are unchanged.
 */

import { useQuery } from '@tanstack/react-query';
import { roomsApi, type ClubRoomsResponse } from '../lib/api/rooms';

export type {
  Room,
  RoomCategory,
  TrainingGroupStatus,
} from '../lib/api/rooms';
export { getRoomCategory } from '../lib/api/rooms';

/**
 * Fetch all accessible rooms for a club
 * Returns: Lobby + all events/training groups user has access to
 *
 * Error Handling:
 * - 403: User not a club member (should be impossible if using requireAuth)
 * - 404: Club not found
 * - Network errors: Retry 2x with exponential backoff
 *
 * Retry Policy:
 * - Network errors: 2 retries
 * - 403/404: No retries (immediate fail, show error state)
 */
export function useClubRooms(clubId: string | undefined) {
  return useQuery<ClubRoomsResponse, Error>({
    queryKey: ['club-rooms', clubId],
    queryFn: async () => {
      if (!clubId) {
        throw new Error('Club ID is required');
      }
      return roomsApi.getClubRooms(clubId);
    },
    enabled: !!clubId,
    staleTime: 2 * 60 * 1000, // 2 minutes (rooms change when events/groups created)
    retry: (failureCount, error: Error & { status?: number }) => {
      // Don't retry on 403 (forbidden) or 404 (not found)
      if (error?.status === 403 || error?.status === 404) {
        return false;
      }
      // Retry up to 2 times for network errors
      return failureCount < 2;
    },
  });
}
